export default class ScoreBasedRecaptcha {
    /**
     * Recaptcha constructor
     *
     * @param config
     */
    constructor(config) {
        this.setConfig(config);

        if (!this.checkIfRecaptchaIsLoaded()) {
            const script = document.createElement('script');
            script.id = 'recaptcha-script';
            script.src = `https://www.google.com/recaptcha/enterprise.js?render=${this.siteKey}`;
            script.onerror = this.handleScriptError;
            document.head.appendChild(script);
        }
    }

    /**
     * Handle recaptcha script append error
     *
     * @param err
     */
    handleScriptError(err) {
        console.error('There was an error on loading the recaptcha script.', err);
    }

    /**
     * Get the recaptcha token for a given action
     */
    getToken() {
        try {
            // check if grecaptcha is loaded
            if (this.checkIfRecaptchaIsLoaded() === false) {
                return Promise.reject('Recaptcha is not loaded.');
            }

            // track recaptcha view
            this.trackRecaptchaView();

            // get the token for the given action
            return new Promise((resolve, reject) => {
                grecaptcha.enterprise.ready(() => {
                    grecaptcha.enterprise
                        .execute(this.siteKey, { action: this.action })
                        .then((token) => {
                            // track recaptcha success
                            this.trackRecaptchaSuccess();

                            resolve(token);
                        })
                        .catch((err) => {
                            reject(err);
                        });
                });
            });
        } catch (error) {
            // track recaptcha failure
            this.trackRecaptchaFailure();

            return Promise.reject(error);
        }
    }

    /**
     * Set recaptcha config
     *
     * @param config
     */
    setConfig(config) {
        if (this.configIsValid(config)) {
            this.setSiteKey(config.siteKey)
                .setAction(config.action)
                .setTrackingService(config.trackingService);
        } else {
            console.error('Recaptcha: invalid config.');
        }
    }

    /**
     * Check if config is valid
     *
     * @param config
     * @returns {boolean}
     */
    configIsValid(config) {
        return config?.siteKey !== undefined
            && config?.action !== undefined
            && config?.trackingService !== undefined;
    }

    /**
     * Set tracking service
     *
     * @param trackingService
     * @returns {Recaptcha}
     */
    setTrackingService(trackingService) {
        this.trackingService = trackingService;
        return this;
    }

    /**
     * Set site key
     *
     * @returns {Recaptcha}
     */
    setSiteKey(siteKey) {
        this.siteKey = siteKey;
        return this;
    }

    /**
     * Set action
     *
     * @returns {Recaptcha}
     */
    setAction(action) {
        this.action = action.toUpperCase().trim();
        return this;
    }

    /**
     * Check if grecaptcha is loaded
     *
     * @throws Error if grecaptcha is not loaded
     */
    checkIfRecaptchaIsLoaded() {
        const ownProp = Object.prototype.hasOwnProperty;
        return ownProp.call(window, 'grecaptcha') &&
            ownProp.call(window.grecaptcha, 'enterprise') &&
            ownProp.call(window.grecaptcha.enterprise, 'render');
    }

    /**
     * Track open modal action
     */
    trackRecaptchaView() {
        this.trackingService.view();
    }

    /**
     * Track checkbox click action
     */
    trackRecaptchaVerified() {
        this.trackingService.verified();
    }

    /**
     * Track recaptcha validation success action
     */
    trackRecaptchaSuccess() {
        this.trackingService.success();
    }

    /**
     * Track recaptcha validation failure action
     */
    trackRecaptchaFailure() {
        this.trackingService.failure();
    }
}
